import "@/App.css";
import { 
  Layers,
  ShieldCheck, 
  FileCheck, 
  HardHat, 
  ClipboardList,
  Phone,
  Mail,
  ChevronDown,
  Zap,
  Building2,
  Shield,
  ClipboardCheck,
  MessageCircle,
  CheckCircle2,
  ArrowRight,
  Briefcase
} from "lucide-react";
import { Button } from "./components/ui/button";

const WHATSAPP_NUMBER = "5542999595433";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=Olá, gostaria de solicitar uma avaliação técnica.`;
const EMAIL = "jean@martinsemendonca.com.br";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_martins-eletricista/artifacts/j4cncopc_WhatsApp_Image_2026-02-24_at_18.39.02-2-removebg-preview.png";
const CREA_LOGO = "https://customer-assets.emergentagent.com/job_martins-eletricista/artifacts/t534nx9b_WhatsApp_Image_2026-02-24_at_18.39.02-removebg-preview.png";

// Hero Background - Técnico realizando medição
const HERO_BG = "https://customer-assets.emergentagent.com/job_martins-eletricista/artifacts/mbru23p2_WhatsApp%20Image%202026-02-24%20at%2009.56.39-3.jpeg";

// Foto do painel elétrico para seção Sobre
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_martins-eletricista/artifacts/6e1aj8tt_WhatsApp%20Image%202026-02-24%20at%2009.56.40.jpeg";

// Subestação para seção Diferencial
const SUBSTATION_IMG = "https://customer-assets.emergentagent.com/job_martins-eletricista/artifacts/lwxv9jrq_WhatsApp%20Image%202026-02-24%20at%2009.56.39-2.jpeg";

// Hero Section
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="hero-section"
      style={{
        backgroundImage: `url('${HERO_BG}')`
      }}
    >
      <div className="hero-overlay" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-4xl">
          {/* Label */}
          <div className="opacity-0 animate-fade-in-up">
            <span className="text-label text-amber-400 mb-4 block">
              Consultoria e Engenharia
            </span>
          </div>
          
          {/* Company Name */}
          <h1 
            className="opacity-0 animate-fade-in-up animation-delay-200 text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            MARTINS & MENDONÇA<br />
            <span className="text-amber-400">ENGENHARIA</span>
          </h1>
          
          {/* Tagline */}
          <h2 className="opacity-0 animate-fade-in-up animation-delay-200 text-xl sm:text-2xl font-semibold text-slate-300 mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Soluções em Engenharia e Projetos
          </h2>
          
          {/* Subheadline */}
          <p className="opacity-0 animate-fade-in-up animation-delay-400 text-base sm:text-lg text-slate-300 mb-10 max-w-3xl leading-relaxed">
            Soluções em engenharia elétrica, mecânica e segurança do trabalho com atuação consultiva 
            e assessoria técnica especializada, garantindo conformidade normativa, mitigação de riscos 
            e gestão estratégica de projetos e obras.
          </p>
          
          {/* CTA Buttons - Padronizados */}
          <div className="opacity-0 animate-fade-in-up animation-delay-600 flex flex-col sm:flex-row gap-4 flex-wrap">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button 
                data-testid="hero-cta-primary-btn"
                className="btn-primary h-14 px-8 text-sm rounded-sm w-full uppercase tracking-wide font-bold"
              >
                <ClipboardCheck className="w-5 h-5 mr-2 flex-shrink-0" />
                Solicitar Avaliação Técnica
              </Button>
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button 
                data-testid="hero-whatsapp-btn"
                className="btn-whatsapp h-14 px-8 text-sm rounded-sm w-full uppercase tracking-wide font-bold"
              >
                <MessageCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                Falar com um Especialista
              </Button>
            </a>
            <a href="#servicos" className="w-full sm:w-auto">
              <Button 
                data-testid="hero-services-btn"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 h-14 px-8 text-sm rounded-sm uppercase tracking-wide font-bold w-full"
              >
                <ArrowRight className="w-5 h-5 mr-2 flex-shrink-0" />
                Conferir Nossas Atividades
              </Button>
            </a>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <a href="#sobre" className="scroll-indicator text-white/60 hover:text-white transition-colors">
          <ChevronDown className="w-8 h-8" />
        </a>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const highlights = [
    { icon: Building2, text: "Engenharia elétrica, mecânica e civil" },
    { icon: Shield, text: "Segurança do trabalho" },
    { icon: ClipboardCheck, text: "Conformidade normativa" },
    { icon: Briefcase, text: "Gestão estratégica de projetos" },
  ];

  return (
    <section id="sobre" data-testid="about-section" className="section-light py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[4/3] overflow-hidden rounded-sm">
              <img 
                src={ABOUT_IMG}
                alt="Equipe de engenharia em ação"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Accent bar */}
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-amber-400 -z-10 rounded-sm" />
          </div>
          
          {/* Content */}
          <div>
            <span className="text-label text-amber-500 mb-4 block">Sobre a Empresa</span>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Experiência e Excelência<br />em Engenharia
            </h2>
            <p className="text-slate-600 mb-8 leading-relaxed">
              A <strong>Martins & Mendonça Engenharia</strong> possui atuação consolidada em projetos, laudos, 
              estudos de proteção e gestão técnica de obras nas áreas elétrica, mecânica, civil e 
              segurança do trabalho. Com experiência aplicada ao setor industrial e agroindustrial, 
              integra conhecimento técnico aprofundado, visão estratégica e rigor normativo para 
              garantir confiabilidade operacional e proteção de ativos.
            </p>
            
            {/* Highlights grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {highlights.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-slate-50 rounded-sm">
                  <div className="icon-wrapper flex-shrink-0">
                    <item.icon className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
                  </div>
                  <span className="text-sm text-slate-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Data - Updated
const servicesData = [
  {
    id: "multidisciplinar",
    title: "Engenharia Multidisciplinar Integrada",
    icon: Layers,
    featured: true,
    description: "Desenvolvimento de soluções técnicas integradas nas áreas elétrica, mecânica e civil, com abordagem multidisciplinar voltada à segurança, desempenho operacional e conformidade normativa.",
    subDescription: "Atuamos de forma estruturada na concepção, dimensionamento e compatibilização de sistemas técnicos, garantindo integração entre infraestrutura elétrica, sistemas mecânicos e elementos construtivos.",
    items: [
      "Projetos elétricos industriais e comerciais",
      "Subestações, entradas de energia e adequações junto à concessionária",
      "Projetos luminotécnicos e eficiência energética",
      "Sistemas de climatização e HVAC",
      "Sistemas de geração, backup e confiabilidade energética",
      "Projetos e adequações de infraestrutura civil e mecânica"
    ],
    image: "https://images.unsplash.com/photo-1713557112617-e12d67bddc3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxlbGVjdHJpY2FsJTIwY2lyY3VpdCUyMHNjaGVtYXRpY3MlMjBibHVlcHJpbnRzfGVufDB8fHx8MTc3MTg1MjA3Nnww&ixlib=rb-4.1.0&q=85"
  },
  {
    id: "estudos",
    title: "Estudos Técnicos e Segurança Operacional",
    icon: ShieldCheck,
    description: "Análises técnicas especializadas voltadas à mitigação de riscos e confiabilidade de sistemas.",
    items: [
      "Estudo de proteção e seletividade",
      "Estudos de curto-circuito",
      "Cálculo e estudo de ATPV (Arc Flash)",
      "Projetos e inspeções de SPDA e PPCI",
      "Avaliação de risco elétrico",
      "Análises técnicas fundamentadas"
    ]
  },
  {
    id: "laudos",
    title: "Laudos, Perícias e Apoio Técnico Jurídico",
    icon: FileCheck,
    description: "Atuação técnica com embasamento normativo e responsabilidade profissional formal.",
    items: [
      "Laudos técnicos NR10 e NR12",
      "Laudos de instalações elétricas",
      "PPCI elétrico",
      "Pareceres técnicos",
      "Perícias técnicas",
      "Assistência técnica em processos judiciais",
      "Emissão de ART"
    ]
  },
  {
    id: "gestao",
    title: "Gestão e Execução de Obras",
    icon: HardHat,
    description: "Planejamento, coordenação e acompanhamento técnico de empreendimentos.",
    items: [
      "Gestão técnica de obras de capital (CAPEX)",
      "Fiscalização e acompanhamento de execução",
      "Planejamento e controle técnico",
      "Obras elétricas, mecânicas e civis",
      "Coordenação de equipes e fornecedores"
    ]
  },
  {
    id: "documentacao",
    title: "Documentação e Conformidade Técnica",
    icon: ClipboardList,
    description: "Estruturação documental para segurança, auditorias e regularizações.",
    items: [
      "Adequações às normas técnicas",
      "Documentação técnica de segurança",
      "Procedimentos operacionais",
      "Suporte técnico para auditorias"
    ]
  }
];

// Services Section
const ServicesSection = () => {
  return (
    <section id="servicos" data-testid="services-section" className="section-surface py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-label text-amber-500 mb-4 block">Nossas Soluções em Engenharia</span>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Serviços Especializados
          </h2>
        </div>
        
        {/* Bento Grid */}
        <div className="bento-grid">
          {servicesData.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Service Card Component
const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  const isFeatured = service.featured;
  
  const gridClass = isFeatured 
    ? "md:col-span-2 md:row-span-2" 
    : "";

  return (
    <div 
      data-testid={`service-card-${service.id}`}
      className={`service-card bg-white p-6 lg:p-8 rounded-sm ${gridClass} ${isFeatured ? 'service-card-featured' : ''}`}
    >
      {/* Header */}
      <div className={`flex items-start gap-4 mb-4 ${isFeatured ? 'text-white' : ''}`}>
        <div className={`icon-wrapper ${isFeatured ? 'icon-wrapper-dark' : ''}`}>
          <Icon className={`w-6 h-6 ${isFeatured ? 'text-amber-400' : 'text-amber-500'}`} strokeWidth={1.5} />
        </div>
        <h3 
          className={`text-xl lg:text-2xl font-bold tracking-tight ${isFeatured ? 'text-white' : 'text-slate-900'}`}
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          {service.title}
        </h3>
      </div>
      
      {/* Description */}
      {service.description && (
        <p className={`mb-4 text-sm ${isFeatured ? 'text-slate-300' : 'text-slate-600'}`}>
          {service.description}
        </p>
      )}
      
      {/* Sub Description for featured */}
      {isFeatured && service.subDescription && (
        <p className="mb-4 text-sm text-slate-400 italic">
          {service.subDescription}
        </p>
      )}
      
      {/* Featured image */}
      {isFeatured && service.image && (
        <div className="mb-6 rounded-sm overflow-hidden">
          <img 
            src={service.image} 
            alt={service.title}
            className="w-full h-40 object-cover"
          />
        </div>
      )}
      
      {/* Items list */}
      <ul className={`space-y-2 ${isFeatured ? 'text-slate-300' : 'text-slate-600'}`}>
        {service.items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm">
            <Zap className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isFeatured ? 'text-amber-400' : 'text-amber-500'}`} strokeWidth={1.5} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Diferencial Section - Com imagem de subestação
const DiferencialSection = () => {
  const diferenciais = [
    "Engenharia com responsabilidade técnica",
    "Conformidade com normas nacionais",
    "Foco em segurança e mitigação de riscos",
    "Experiência em ambientes industriais e agroindustriais",
    "Gestão estruturada de projetos"
  ];

  return (
    <section data-testid="diferencial-section" className="section-light py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div>
            <span className="text-label text-amber-500 mb-4 block">Diferencial Competitivo</span>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8 tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Por que escolher a<br />Martins & Mendonça?
            </h2>
            
            {/* Diferenciais list */}
            <ul className="space-y-4 mb-8">
              {diferenciais.map((item, index) => (
                <li key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-amber-500 flex-shrink-0" strokeWidth={2} />
                  <span className="text-slate-700 font-medium">{item}</span>
                </li>
              ))}
            </ul>
            
            {/* Final phrase */}
            <div className="bg-slate-900 p-6 rounded-sm">
              <p className="text-white text-lg font-medium leading-relaxed" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                "Engenharia que protege seu patrimônio, garante conformidade e sustenta a continuidade da sua operação."
              </p>
            </div>
          </div>
          
          {/* Image - Subestação */}
          <div className="relative">
            <div className="aspect-[4/3] overflow-hidden rounded-sm">
              <img 
                src={SUBSTATION_IMG}
                alt="Subestação elétrica de alta tensão"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Accent bar */}
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-amber-400 -z-10 rounded-sm" />
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section data-testid="cta-section" className="cta-section section-dark py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="text-label text-amber-400 mb-4 block">Entre em Contato</span>
        <h2 
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          Precisa de suporte técnico especializado em engenharia?
        </h2>
        <p className="text-slate-400 mb-10 max-w-2xl mx-auto">
          Solicite uma proposta técnica personalizada para o seu projeto. 
          Atendimento ágil e consultoria especializada.
        </p>
        
        {/* CTA Buttons - Padronizados */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="cta-whatsapp-btn"
              className="btn-whatsapp h-14 px-10 text-sm rounded-sm w-full sm:w-auto uppercase tracking-wide font-bold"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Falar via WhatsApp
            </Button>
          </a>
          <a href={`mailto:${EMAIL}`}>
            <Button 
              data-testid="cta-email-btn"
              className="btn-primary h-14 px-10 text-sm rounded-sm w-full sm:w-auto uppercase tracking-wide font-bold"
            >
              <Mail className="w-5 h-5 mr-2" />
              Solicitar Proposta
            </Button>
          </a>
        </div>
        
        {/* Contact Info */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-slate-400">
          <a href={`tel:+${WHATSAPP_NUMBER}`} className="flex items-center gap-2 hover:text-white transition-colors">
            <Phone className="w-5 h-5 text-amber-400" />
            <span>(42) 99959-5433</span>
          </a>
          <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 hover:text-white transition-colors">
            <Mail className="w-5 h-5 text-amber-400" />
            <span>{EMAIL}</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer com CREA - usando texto estilizado
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-8" style={{ backgroundColor: '#1d2339' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            {/* Logo da Empresa */}
            <img 
              src={LOGO_URL} 
              alt="Martins & Mendonça Engenharia" 
              className="h-14 w-auto object-contain"
            />
            {/* CREA Logo */}
            <img 
              src={CREA_LOGO} 
              alt="CREA - Conselho Regional de Engenharia e Agronomia" 
              className="h-12 w-auto object-contain"
            />
          </div>
          <div className="text-center md:text-left">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Martins & Mendonça Engenharia
            </p>
            <p className="text-slate-500 text-xs mt-1">
              Consultoria e Engenharia
            </p>
          </div>
          <div className="flex items-center gap-6">
            <a 
              href={WHATSAPP_LINK} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-amber-400 transition-colors"
              data-testid="footer-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
            </a>
            <a 
              href={`mailto:${EMAIL}`}
              className="text-slate-400 hover:text-amber-400 transition-colors"
              data-testid="footer-email"
            >
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Navigation Header - Atualizado com Eng. Jean Carlos Martins
const Header = () => {
  return (
    <header data-testid="header" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm border-b border-slate-700" style={{ backgroundColor: '#1d2339' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo e Info do Responsável Técnico */}
          <a href="#" className="flex items-center gap-4">
            <img 
              src={LOGO_URL} 
              alt="Martins & Mendonça Engenharia" 
              className="h-14 w-auto object-contain"
            />
            <div className="hidden sm:block border-l border-slate-600 pl-4">
              <p className="text-white font-bold text-base" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Eng. Jean Carlos Martins
              </p>
              <p className="text-slate-400 text-xs uppercase tracking-wider">Responsável Técnico</p>
            </div>
          </a>
          
          {/* Nav Links - Hidden on mobile */}
          <nav className="hidden md:flex items-center gap-6">
            <a href="#sobre" className="text-slate-300 hover:text-white text-sm font-medium transition-colors">
              Sobre
            </a>
            <a href="#servicos" className="text-slate-300 hover:text-white text-sm font-medium transition-colors">
              Serviços
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button data-testid="header-cta-btn" className="btn-primary h-10 px-6 text-xs rounded-sm uppercase tracking-wide font-bold">
                Contato
              </Button>
            </a>
          </nav>
          
          {/* Mobile CTA */}
          <div className="md:hidden">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button data-testid="header-mobile-cta-btn" className="btn-whatsapp h-10 px-4 text-xs rounded-sm uppercase tracking-wide font-bold">
                <MessageCircle className="w-4 h-4 mr-1" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  return (
    <a 
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-whatsapp"
      className="fixed bottom-6 right-6 z-[999999] bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 animate-pulse-whatsapp"
      aria-label="Falar no WhatsApp"
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 24 24" 
        className="w-8 h-8 fill-white"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <DiferencialSection />
        <CTASection />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
